import { FC } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Grid2, Chip } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DevicesIcon from '@mui/icons-material/Devices';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LinkIcon from '@mui/icons-material/Link';

import {
  useGetNiadaTotalVisitsQuery,
  useGetNiadaVisitsByPeriodQuery,
  useGetNiadaDeviceBreakdownQuery,
  useGetNiadaTopReferrersQuery,
  useGetExcelsusTotalVisitsQuery,
  useGetExcelsusVisitsByPeriodQuery,
} from '@carcareers/store';

export const CampaignStatistics: FC = () => {
  const { data: totalVisits, isLoading: isLoadingTotal } = useGetNiadaTotalVisitsQuery();
  const { data: visitsByWeek, isLoading: isLoadingByPeriod } = useGetNiadaVisitsByPeriodQuery({ period: 'week' });
  const { data: deviceBreakdown, isLoading: isLoadingDevices } = useGetNiadaDeviceBreakdownQuery();
  const { data: topReferrers, isLoading: isLoadingReferrers } = useGetNiadaTopReferrersQuery({ limit: 5 });

  const { data: excelsusTotal, isLoading: isLoadingExcelsusTotal } = useGetExcelsusTotalVisitsQuery();
  const { data: excelsusWeeks, isLoading: isLoadingExcelsusWeeks } = useGetExcelsusVisitsByPeriodQuery({
    period: 'week',
  });
  const excelsusWeeksSafe = excelsusWeeks ?? [];

  const formatNumber = (value?: number) => {
    if (!value) return '0';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <QrCode2Icon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h2" sx={{ fontWeight: 600 }}>
          NIADA QR Code Campaign
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid2 container spacing={3} sx={{ mb: 3 }}>
        {/* Total Visits */}
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Unique Visits
                </Typography>
              </Box>
              {isLoadingTotal ? (
                <Skeleton variant="text" width={80} height={48} />
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formatNumber(totalVisits?.total)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* This Week */}
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                This Week
              </Typography>
              {isLoadingByPeriod ? (
                <Skeleton variant="text" width={60} height={48} />
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {formatNumber(visitsByWeek?.[0]?.count || 0)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Last Week */}
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Last Week
              </Typography>
              {isLoadingByPeriod ? (
                <Skeleton variant="text" width={60} height={48} />
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {formatNumber(visitsByWeek?.[1]?.count || 0)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Total Weeks */}
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Total Weeks Active
              </Typography>
              {isLoadingByPeriod ? (
                <Skeleton variant="text" width={40} height={48} />
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {visitsByWeek?.length || 0}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      <Grid2 container spacing={3}>
        {/* Device Breakdown */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <DevicesIcon color="primary" />
                <Typography variant="h6">Device Breakdown</Typography>
              </Box>
              {isLoadingDevices ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Skeleton variant="rectangular" height={60} />
                  <Skeleton variant="rectangular" height={60} />
                  <Skeleton variant="rectangular" height={60} />
                </Box>
              ) : deviceBreakdown && deviceBreakdown.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {deviceBreakdown.map((device, index) => {
                    const percentage =
                      totalVisits?.total && totalVisits.total > 0
                        ? ((device.count / totalVisits.total) * 100).toFixed(1)
                        : '0';
                    return (
                      <Box key={index}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                            {device.deviceType}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              {percentage}%
                            </Typography>
                            <Typography variant="h6">{formatNumber(device.count)}</Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: 8,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${percentage}%`,
                              height: '100%',
                              bgcolor: 'primary.main',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No device data available yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Top Referrers */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <LinkIcon color="primary" />
                <Typography variant="h6">Top Traffic Sources</Typography>
              </Box>
              {isLoadingReferrers ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Skeleton variant="rectangular" height={50} />
                  <Skeleton variant="rectangular" height={50} />
                  <Skeleton variant="rectangular" height={50} />
                </Box>
              ) : topReferrers && topReferrers.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {topReferrers.map((referrer, index) => (
                    <Box key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1.5,
                          px: 2,
                          bgcolor: 'grey.100',
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            sx={{
                              bgcolor: index === 0 ? 'primary.light' : 'grey.400',
                              color: 'white',
                              fontWeight: 700,
                              minWidth: 32,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: index === 0 ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {referrer.referrer === '' || referrer.referrer === 'direct'
                              ? 'Direct / QR Code'
                              : referrer.referrer}
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, ml: 2 }}>
                          {formatNumber(referrer.count)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No referrer data available yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Weekly Trend */}
        <Grid2 size={{ xs: 12 }}>
          <Card elevation={0} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Weekly Visit Trend
              </Typography>
              {isLoadingByPeriod ? (
                <Skeleton variant="rectangular" height={200} />
              ) : visitsByWeek && visitsByWeek.length > 0 ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ display: 'flex', gap: 2, minWidth: 'max-content', pb: 2 }}>
                    {visitsByWeek
                      .slice()
                      .reverse()
                      .map((week, index) => {
                        const maxCount = Math.max(...visitsByWeek.map((w) => w.count));
                        const heightPercent = maxCount > 0 ? (week.count / maxCount) * 100 : 0;
                        return (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 1,
                              minWidth: 80,
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {formatNumber(week.count)}
                            </Typography>
                            <Box
                              sx={{
                                width: 60,
                                height: 150,
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  height: `${heightPercent}%`,
                                  bgcolor: 'primary.main',
                                  transition: 'height 0.3s ease',
                                  borderRadius: '0 0 8px 8px',
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                              {week.period}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No weekly data available yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* Excelsus QR Code Campaign */}
      <Box sx={{ mt: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <QrCode2Icon sx={{ fontSize: 32, color: 'secondary.main' }} />
          <Typography variant="h2" sx={{ fontWeight: 600 }}>
            Excelsus QR Code Campaign
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            carcareers.com/go/excelsus → excelsusdata.com
          </Typography>
        </Box>

        <Grid2 container spacing={3} sx={{ mb: 3 }}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingUpIcon color="secondary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Unique Scans
                  </Typography>
                </Box>
                {isLoadingExcelsusTotal ? (
                  <Skeleton variant="text" width={80} height={48} />
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                    {formatNumber(excelsusTotal?.total)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  This Week
                </Typography>
                {isLoadingExcelsusWeeks ? (
                  <Skeleton variant="text" width={60} height={48} />
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {formatNumber(excelsusWeeks?.[0]?.count || 0)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Last Week
                </Typography>
                {isLoadingExcelsusWeeks ? (
                  <Skeleton variant="text" width={60} height={48} />
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {formatNumber(excelsusWeeks?.[1]?.count || 0)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Total Weeks Active
                </Typography>
                {isLoadingExcelsusWeeks ? (
                  <Skeleton variant="text" width={40} height={48} />
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {excelsusWeeks?.length || 0}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        <Card elevation={0} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Weekly Scan Trend
            </Typography>
            {isLoadingExcelsusWeeks ? (
              <Skeleton variant="rectangular" height={200} />
            ) : excelsusWeeksSafe.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'flex', gap: 2, minWidth: 'max-content', pb: 2 }}>
                  {excelsusWeeksSafe
                    .slice()
                    .reverse()
                    .map((week, index) => {
                      const maxCount = Math.max(...excelsusWeeksSafe.map((w) => w.count));
                      const heightPercent = maxCount > 0 ? (week.count / maxCount) * 100 : 0;
                      return (
                        <Box
                          key={index}
                          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 80 }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {formatNumber(week.count)}
                          </Typography>
                          <Box
                            sx={{
                              width: 60,
                              height: 150,
                              bgcolor: 'grey.100',
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-end',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: '100%',
                                height: `${heightPercent}%`,
                                bgcolor: 'secondary.main',
                                transition: 'height 0.3s ease',
                                borderRadius: '0 0 8px 8px',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                            {week.period}
                          </Typography>
                        </Box>
                      );
                    })}
                </Box>
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No scan data available yet
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default CampaignStatistics;
